import { useEffect } from "react";
import { AnimatePresence, motion, useMotionTemplate, useMotionValue, useSpring } from "framer-motion";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { MessageCircleMore } from "lucide-react";
import { footerDetails } from "@/components/equinox/data";
import { CursorAura, SiteLoader } from "@/components/equinox/MotionEnhancers";
import SiteFooter from "@/components/equinox/SiteFooter";
import SiteHeader from "@/components/equinox/SiteHeader";
import { initializeAdTracking, trackAdClickEvent, trackLeadEvent } from "@/lib/leadTracking";

export default function MarketingLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const pointerX = useMotionValue(960);
  const pointerY = useMotionValue(260);
  const smoothX = useSpring(pointerX, { damping: 38, stiffness: 240 });
  const smoothY = useSpring(pointerY, { damping: 38, stiffness: 240 });
  const silverGlow = useMotionTemplate`radial-gradient(620px circle at ${smoothX}px ${smoothY}px, rgba(255, 255, 255, 0.07), transparent 42%)`;
  const sapphireGlow = useMotionTemplate`radial-gradient(520px circle at ${smoothX}px ${smoothY}px, rgba(37, 99, 235, 0.11), transparent 36%)`;

  const pageIntent = {
    "/": { label: "Get Your Growth Blueprint", href: "/contact" },
    "/aso-ai-engine": { label: "Generate AI ASO Report", href: "/aso-ai-engine" },
    "/services": { label: "Audit My App →", href: "/contact" },
    "/case-studies": { label: "See Case Studies", href: "/case-studies" },
    "/gives-back": { label: "Choose Your Cause", href: "/gives-back#cause-selection" },
    "/about": { label: "Book a Growth Call", href: "/contact" },
    "/insights": { label: "Get Your Growth Blueprint", href: "/contact" },
    "/contact": { label: "Book a Free Growth Call", href: "/contact" },
  };
  const currentCta = pageIntent[location.pathname] || { label: "Book a Growth Call", href: "/contact" };

  const safeScrollToTop = (smooth = false) => {
    if (typeof window === "undefined") {
      return;
    }

    try {
      if (typeof window.scrollTo === "function") {
        if (smooth) {
          window.scrollTo({ top: 0, behavior: "smooth" });
        } else {
          window.scrollTo(0, 0);
        }
      }
    } catch (error) {
      console.warn("Window scrollTo unavailable", error);
    }

    try {
      if (typeof document?.documentElement?.scrollTo === "function") {
        document.documentElement.scrollTo(0, 0);
      }
    } catch (error) {
      console.warn("documentElement scrollTo unavailable", error);
    }

    try {
      if (typeof document?.body?.scrollTo === "function") {
        document.body.scrollTo(0, 0);
      }
    } catch (error) {
      console.warn("body scrollTo unavailable", error);
    }
  };

  const handlePrimaryAction = () => {
    if (currentCta.href === location.pathname) {
      safeScrollToTop(true);
      return;
    }
    navigate(currentCta.href);
  };

  useEffect(() => {
    safeScrollToTop();
  }, [location.pathname]);

  useEffect(() => {
    initializeAdTracking();
    trackLeadEvent("page_view", {
      label: location.pathname,
      metadata: { page_path: location.pathname },
    });
  }, [location.pathname, location.search]);

  return (
    <main
      className="page-shell relative overflow-hidden"
      onMouseMove={(event) => {
        pointerX.set(event.clientX);
        pointerY.set(event.clientY);
      }}
    >
      <SiteLoader />
      <CursorAura pointerX={pointerX} pointerY={pointerY} />
      <motion.div className="pointer-events-none fixed inset-0 z-[1] opacity-90 mix-blend-screen" style={{ background: silverGlow }} />
      <motion.div className="pointer-events-none fixed inset-0 z-[1] opacity-80 mix-blend-screen" style={{ background: sapphireGlow }} />

      <div className="relative z-10">
        <SiteHeader
          ctaLabel={currentCta.label}
          onPrimaryAction={() => {
            trackAdClickEvent({ label: currentCta.label, metadata: { placement: "site_header", target: currentCta.href } });
            handlePrimaryAction();
          }}
        />
        <AnimatePresence mode="wait">
          <motion.div
            key={location.pathname}
            initial={{ opacity: 0, y: 48, scale: 0.985, filter: "blur(14px)" }}
            animate={{ opacity: 1, y: 0, scale: 1, filter: "blur(0px)" }}
            exit={{ opacity: 0, y: -32, scale: 0.992, filter: "blur(10px)" }}
            transition={{ duration: 0.72, ease: [0.22, 1, 0.36, 1] }}
            data-testid="route-transition-shell"
          >
            <Outlet context={{ onPrimaryAction: handlePrimaryAction }} />
          </motion.div>
        </AnimatePresence>
        <SiteFooter
          ctaLabel={currentCta.label}
          onPrimaryAction={() => {
            trackAdClickEvent({ label: currentCta.label, metadata: { placement: "site_footer", target: currentCta.href } });
            handlePrimaryAction();
          }}
        />
        <a
          href={footerDetails.whatsappHref}
          target="_blank"
          rel="noreferrer"
          onClick={() => trackAdClickEvent({ label: "floating_whatsapp_button", eventName: "whatsapp_click", metadata: { placement: "floating_button" } })}
          className="whatsapp-float group fixed bottom-6 right-6 z-40 inline-flex h-16 w-16 items-center justify-center rounded-full border border-emerald-300/35 bg-emerald-400/18 text-white shadow-[0_18px_50px_rgba(16,185,129,0.24)] backdrop-blur-2xl transition-transform duration-300 hover:scale-110"
          data-testid="floating-whatsapp-button"
          aria-label="Open WhatsApp"
        >
          <span className="whatsapp-float-ring absolute inset-0 rounded-full border border-emerald-300/30" />
          <MessageCircleMore className="relative z-10 h-7 w-7 text-emerald-100" />
        </a>
      </div>
    </main>
  );
}